import L from "leaflet";
import { get, post, api, ApiError } from "./api";
import * as store from "./store";
import { h, toast, uuid } from "./util";
import { I } from "./icons";
import { currentPosition } from "./map";
import { isDemo } from "./demo";
import { DRUH_POZEMKU, type Parcel } from "./types";
import type { Ctx } from "./garden";

type Found = Omit<Parcel, "id" | "garden_id">;

const chevronUp = "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.4\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><path d=\"m6 15 6-6 6 6\"/></svg>";

export function openParcels(ctx: Ctx) {
  const sheet = ctx.root.querySelector("#parcelsSheet") as HTMLElement;
  const { map, garden } = ctx;
  const preview = L.featureGroup().addTo(map);
  let tapMode = false;
  let lastKu: { kod: number; nazev: string } | null = garden.parcels[0] && garden.parcels[0].ku_kod ? { kod: garden.parcels[0].ku_kod, nazev: garden.parcels[0].ku_nazev } : null;

  const list = h("ul", { class: "tl" });
  const status = h("p", { class: "hint", role: "status" });
  const confirmBox = h("div", { class: "ai", hidden: true });
  const kuInput = h("input", { id: "kuInput", placeholder: "např. Lysá nad Labem", autocomplete: "off", value: lastKu?.nazev ?? "" }) as HTMLInputElement;
  const kuSuggest = h("ul", { class: "suggest", hidden: true });
  const cisloInput = h("input", { id: "cisloInput", placeholder: "např. 1532/4", autocomplete: "off" }) as HTMLInputElement;

  // compact bar shown instead of the sheet while picking a parcel in the map
  const tapText = h("p", {});
  const tapActions = h("div", { class: "btns" });
  const tapBar = h("div", { class: "tapbar", hidden: true, role: "region", "aria-label": "Výběr parcely v mapě" },
    h("div", { class: "row-between" }, tapText,
      h("button", { type: "button", class: "btn ghost icon-only", title: "Zpět na zadávání", "aria-label": "Zpět na zadávání parcel", onclick: () => setTap(false), html: chevronUp })),
    tapActions);
  ctx.root.append(tapBar);

  const numForm = h("form", { class: "card", style: "box-shadow:none;padding:0" },
    h("div", { class: "row2" },
      h("label", { class: "field" }, h("span", {}, "Katastrální území"), kuInput, kuSuggest),
      h("label", { class: "field" }, h("span", {}, "Číslo parcely"), cisloInput)),
    h("button", { class: "btn ghost", type: "submit", html: I.search + "<span>Najít parcelu</span>" }));

  sheet.replaceChildren(
    h("div", { class: "sheet-head" }, h("h2", {}, "Parcely zahrady"), h("button", { class: "close", "aria-label": "Zavřít", onclick: done }, "×")),
    h("div", { class: "sheet-body" },
      list,
      h("div", { class: "btns even" },
        h("button", { type: "button", class: "btn primary", onclick: byGps, html: I.locate + "<span>Parcela, kde stojím</span>" }),
        h("button", { type: "button", class: "btn ghost", onclick: () => setTap(true), html: I.pin + "<span>Klepnout do mapy</span>" })),
      confirmBox, status,
      h("p", { class: "hint" }, "Nebo zadej katastrální území a číslo parcely (stavební parcely jako „st. 86“):"),
      numForm,
      h("button", { type: "button", class: "btn block", onclick: done }, "Hotovo")));

  function drawList() {
    list.replaceChildren();
    if (!garden.parcels.length) list.append(h("li", { class: "empty" }, "Zatím žádná parcela."));
    for (const p of garden.parcels) {
      list.append(h("li", { class: "prow" },
        h("span", { class: "t" },
          h("b", {}, p.cislo + " · " + p.ku_nazev),
          h("small", { class: "hint" }, [p.druh_pozemku ? DRUH_POZEMKU[p.druh_pozemku] ?? "" : "", p.vymera ? p.vymera.toLocaleString("cs-CZ") + " m²" : ""].filter(Boolean).join(" · "))),
        h("button", { type: "button", class: "btn ghost icon-only", "aria-label": "Odebrat parcelu " + p.cislo, title: "Odebrat parcelu", onclick: () => remove(p), html: I.trash })));
    }
  }

  function setTap(on: boolean) {
    tapMode = on;
    if (on) {
      sheet.hidden = true;
      tapBar.hidden = false;
      tapIdle("Klepni do mapy na parcelu, kterou chceš přidat.");
    } else {
      tapBar.hidden = true;
      preview.clearLayers();
      sheet.hidden = false;
    }
  }
  function tapIdle(text: string) {
    tapText.textContent = text;
    tapActions.replaceChildren(h("button", { type: "button", class: "btn block", onclick: done }, "Hotovo"));
  }
  const onMapClick = (e: L.LeafletMouseEvent) => { if (tapMode) void lookupAt(e.latlng.lat, e.latlng.lng); };
  map.on("click", onMapClick);

  async function byGps() {
    status.textContent = "Zjišťuji polohu…";
    try {
      const f = await currentPosition(15000, 12);
      status.textContent = "Poloha nalezena (přesnost " + Math.round(f.acc) + " m). Hledám parcelu…";
      map.setView([f.lat, f.lng], Math.max(map.getZoom(), 18));
      await lookupAt(f.lat, f.lng);
    } catch (e) { status.textContent = (e as Error).message; }
  }

  async function lookupAt(lat: number, lng: number) {
    if (tapMode) { tapText.textContent = "Hledám parcelu…"; tapActions.replaceChildren(); }
    try {
      const r = await get<{ parcel: Found }>("/api/cuzk/parcel-at?lat=" + lat + "&lng=" + lng);
      offer(r.parcel);
    } catch (e) {
      if (tapMode) tapIdle(errText(e) + " Klepni jinam.");
      else status.textContent = errText(e);
    }
  }

  let kuTimer = 0;
  kuInput.addEventListener("input", () => {
    lastKu = null;
    clearTimeout(kuTimer);
    kuTimer = window.setTimeout(async () => {
      const q = kuInput.value.trim();
      if (q.length < 2) { kuSuggest.hidden = true; return; }
      try {
        const r = await get<{ items: { kod: number; nazev: string; obec: string }[] }>("/api/cuzk/ku?q=" + encodeURIComponent(q));
        kuSuggest.replaceChildren(...r.items.map((k) => h("li", {}, h("button", { type: "button", onclick: () => {
          lastKu = { kod: k.kod, nazev: k.nazev }; kuInput.value = k.nazev; kuSuggest.hidden = true; cisloInput.focus();
        } }, h("b", {}, k.nazev), h("small", {}, "obec " + k.obec + " · kód " + k.kod)))));
        kuSuggest.hidden = !r.items.length;
      } catch { kuSuggest.hidden = true; }
    }, 250);
  });
  numForm.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (!lastKu) { status.textContent = "Vyber katastrální území ze seznamu."; kuInput.focus(); return; }
    if (!cisloInput.value.trim()) { cisloInput.focus(); return; }
    try {
      const r = await get<{ parcel: Found }>("/api/cuzk/parcel?ku=" + lastKu.kod + "&cislo=" + encodeURIComponent(cisloInput.value.trim()));
      offer(r.parcel);
    } catch (e) { status.textContent = errText(e); }
  });

  function offer(p: Found) {
    preview.clearLayers();
    const layer = L.geoJSON(p.geometry as any, { style: { color: "#ff7a00", weight: 3, dashArray: "6 5", fillOpacity: 0.15 } as any, interactive: false }).addTo(preview);
    if (!tapMode) map.fitBounds(layer.getBounds(), { paddingTopLeft: [16, 80], paddingBottomRight: [16, sheet.offsetHeight + 20], maxZoom: 19 });
    lastKu = { kod: p.ku_kod, nazev: p.ku_nazev };
    kuInput.value = p.ku_nazev;
    const already = garden.parcels.some((x) => x.ruian_id === p.ruian_id);
    const desc = [p.druh_pozemku ? DRUH_POZEMKU[p.druh_pozemku] : "", p.vymera ? p.vymera.toLocaleString("cs-CZ") + " m²" : ""].filter(Boolean).join(", ");
    const text = [h("b", {}, "Parcela " + p.cislo), ", k.ú. " + p.ku_nazev + (desc ? " (" + desc + ")" : "") + (already ? ". Už je v zahradě." : "")];
    const cancel = () => { preview.clearLayers(); if (tapMode) tapIdle("Klepni do mapy na parcelu, kterou chceš přidat."); else confirmBox.hidden = true; };
    const actions = [
      h("button", { type: "button", class: "btn ghost", onclick: cancel }, "Zrušit"),
      already ? null : h("button", { type: "button", class: "btn primary", onclick: () => add(p) }, "Přidat do zahrady"),
    ];
    if (tapMode) {
      tapText.replaceChildren(...text);
      tapActions.replaceChildren(...actions.filter(Boolean) as Node[]);
    } else {
      confirmBox.hidden = false;
      confirmBox.replaceChildren(h("p", {}, ...text), h("div", { class: "btns" }, ...actions));
      status.textContent = "";
    }
  }

  async function add(p: Found) {
    try {
      let parcel: Parcel;
      if (isDemo()) parcel = { ...p, id: uuid(), garden_id: garden.id };
      else parcel = (await post<{ parcel: Parcel }>("/api/gardens/" + garden.id + "/parcels", { ruian_id: p.ruian_id })).parcel;
      garden.parcels = [...garden.parcels.filter((x) => x.ruian_id !== parcel.ruian_id), parcel];
      await store.putGarden(garden);
      preview.clearLayers(); confirmBox.hidden = true;
      ctx.drawParcels(); drawList();
      if (tapMode) tapIdle("Parcela " + p.cislo + " přidána. Klepni na další, nebo dej Hotovo.");
      else toast("Parcela " + p.cislo + " přidána");
    } catch (e) {
      if (tapMode) tapIdle(errText(e)); else status.textContent = errText(e);
    }
  }

  async function remove(p: Parcel) {
    try {
      if (!isDemo()) await api("DELETE", "/api/gardens/" + garden.id + "/parcels/" + p.id);
      garden.parcels = garden.parcels.filter((x) => x.id !== p.id);
      await store.putGarden(garden);
      ctx.drawParcels(); drawList();
    } catch (e) { status.textContent = errText(e); }
  }

  function done() {
    map.off("click", onMapClick);
    preview.remove();
    tapBar.remove();
    ctx.showSheet(null);
    ctx.fitGarden();
  }

  drawList();
  ctx.showSheet("parcelsSheet");
}

function errText(e: unknown) {
  if (e instanceof ApiError && e.status === 0) return "Hledání parcel potřebuje internet.";
  return e instanceof Error ? e.message : "Nepovedlo se.";
}